import { useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Badge } from "../components/ui/Primitives";
import { SearchInput } from "../components/ui/Fields";
import { usePagedList } from "../hooks/usePagedList";
import type { CoinLedgerEntry } from "../api/types";

const reasonTone: Record<string, "success" | "danger" | "neutral" | "gold"> = {
  iap_purchase: "success",
  rewarded_ad: "success",
  gift_received: "success",
  refund: "success",
  gift_sent: "danger",
  withdrawal: "danger",
  vip_purchase: "gold",
  // Round 30 — manual Send/Deduct Coins adjustments (admin_adjust_coin_
  // balance, migration 0062) show up in this same ledger, tagged plainly.
  admin_credit: "success",
  admin_debit: "danger",
};

interface LedgerViewProps {
  title: string;
  helperText: string;
  fixedReasons?: string[];
}

/**
 * The same append-only coin_ledger, filtered differently — Gifts,
 * Purchases, Refunds, and Rewards are all real, distinct nav entries
 * rather than one generic table an admin has to remember to filter each
 * time, but there's no separate data model or duplicated backend logic
 * behind any of them.
 */
export function LedgerView({ title, helperText, fixedReasons }: LedgerViewProps) {
  // Round 30 — filters by the public User ID now (what this page actually
  // displays), not the internal UUID — see handleAdminCoinLedger's
  // publicUserId resolution.
  const [publicUserId, setPublicUserId] = useState("");
  const { rows, isLoading, error, page, setPage, hasNextPage, reload } = usePagedList<CoinLedgerEntry>("/admin/coins/ledger", {
    publicUserId: publicUserId || undefined,
    reasons: fixedReasons ? fixedReasons.join(",") : undefined,
  });

  const columns: Column<CoinLedgerEntry>[] = [
    {
      // Round 30 — this used to show the internal Supabase/Auth UUID
      // (row.user_id, truncated to 8 chars) — never meaningful to an
      // admin. Now shows the same canonical public User ID (and username)
      // shown everywhere else in the panel.
      header: "User",
      render: (row) => (
        <div className="flex flex-col">
          <span className="font-medium text-ink">{row.username ?? "—"}</span>
          <span className="font-mono text-xs text-ink-faint">ID: {row.public_user_id ?? "—"}</span>
        </div>
      ),
    },
    {
      header: "Amount",
      render: (row) => (
        <span className={row.amount >= 0 ? "font-semibold text-success" : "font-semibold text-danger"}>
          {row.amount >= 0 ? "+" : ""}
          {row.amount.toLocaleString()}
        </span>
      ),
    },
    { header: "Type", render: (row) => <Badge tone={reasonTone[row.reason] ?? "neutral"}>{row.reason}</Badge> },
    {
      header: "Reference",
      render: (row) => (
        <span className="max-w-[180px] truncate text-xs text-ink-faint">{row.note ?? row.external_ref ?? "—"}</span>
      ),
    },
    { header: "Date", render: (row) => new Date(row.created_at).toLocaleString() },
  ];

  return (
    <AppShell title={title}>
      <p className="mb-4 text-xs text-ink-faint">{helperText}</p>

      <div className="mb-4">
        <SearchInput value={publicUserId} onChange={setPublicUserId} placeholder="Filter by public User ID..." />
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        keyField={(r) => r.id}
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        emptyTitle="No entries found"
        pagination={{ page, pageSize: 20, hasNextPage, onPageChange: setPage }}
      />
    </AppShell>
  );
}
